import { authoringApi } from './authoringApi'
import { componentTag, manifestTag, siteTrafficTag, textUnlessProblem } from './baseApi'
import type { ComponentSummary, Document, Manifest } from './authoringApi'

// Cache identity, layered over the generated client rather than into it.
//
// `authoringApi.ts` is regenerated from the contract on every change, so
// nothing may be edited there. Everything this client decides *about*
// those endpoints — what a cache entry is keyed by, what a write does to
// it — is decided here, where regeneration cannot overwrite it.

export const api = authoringApi.enhanceEndpoints({
  endpoints: {
    getAuthoringOpenApiyaml: {
      // The contract is YAML, and the generated query reads every answer
      // as JSON; it is restated here with the one thing it lacks.
      query: () => ({ url: `/v1/authoring/openapi.yaml`, responseHandler: textUnlessProblem }),
      // Fetched for a download and read once; it is not kept.
      keepUnusedDataFor: 0,
    },

    getManifest: {
      // Per tenant, and no longer a constant. The build's catalogue is
      // the same everywhere; what a tenant adds to it is not, so this is
      // keyed by tenant and invalidated when that tenant writes a type
      // definition — see `definesTypes` below.
      //
      // Still held indefinitely while it is valid: a manifest changes
      // when somebody changes it, which the tag catches, and re-fetching
      // it on a timer would re-read the whole type registry to discover
      // nothing.
      keepUnusedDataFor: Infinity,
      providesTags: (_result, _error, arg) => [manifestTag(arg.tenant)],
    },

    listTenants: {
      providesTags: ['Tenants'],
    },

    listComponents: {
      // Keyed by tenant *and* filter, because `?type=Website` and
      // `?type=Content` are different listings over the same tenant and
      // one cache entry for both would serve the palette a site list.
      providesTags: (_result, _error, arg) => [
        { type: 'ComponentList' as const, id: `${arg.tenant}/${arg.type ?? '*'}` },
      ],
    },

    getAuthoringComponent: {
      providesTags: (_result, _error, arg) => [componentTag(arg.tenant, arg.id)],
    },

    listUsages: {
      // Keyed by the component asked about, and **deliberately not
      // invalidated by any write**. Like the listing it is served from
      // the projection, so a refetch triggered by a command would race
      // the reconciler and redraw the answer from before the placement
      // that just happened. A caller that needs it fresh asks again; a
      // dialog that shows it says it can lag.
      providesTags: (_result, _error, arg) => [{ type: 'Usages' as const, id: `${arg.tenant}/${arg.id}` }],
    },

    listSubmissions: {
      // Keyed by site *and* tab, because enquiries and filtered are two
      // listings over the same site and one cache entry for both would
      // show a reader the other tab's rows when they switch back.
      //
      // Not keyed by cursor: a page is a position in a listing rather
      // than a thing, and a tag per page would leave the second page
      // stale after a resend moved a row out of the first.
      providesTags: (_result, _error, arg) => [
        { type: 'Submissions' as const, id: `${arg.tenant}/${arg.website}/${arg.filtered ? 'filtered' : 'enquiries'}` },
      ],
    },

    getSubmission: {
      providesTags: (_result, _error, arg) => [
        { type: 'Submissions' as const, id: `${arg.tenant}/${arg.website}/${arg.submission}` },
      ],
    },

    judgeSubmission: {
      // Both tabs, because a judgement is the reason a reader is looking
      // at the other one.
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Submissions' as const, id: `${arg.tenant}/${arg.website}/${arg.submission}` },
        { type: 'Submissions' as const, id: `${arg.tenant}/${arg.website}/enquiries` },
        { type: 'Submissions' as const, id: `${arg.tenant}/${arg.website}/filtered` },
      ],
    },

    resendSubmission: {
      // A resend moves the row out of the filtered tab and into the
      // enquiries one, so both listings are wrong until they are re-read.
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Submissions' as const, id: `${arg.tenant}/${arg.website}/${arg.submission}` },
        { type: 'Submissions' as const, id: `${arg.tenant}/${arg.website}/enquiries` },
        { type: 'Submissions' as const, id: `${arg.tenant}/${arg.website}/filtered` },
      ],
    },

    listTenantDomains: {
      // Keyed by tenant, and **not invalidated by a command**, for the
      // reason `listUsages` is not: the bindings in it are the hostname
      // index's, written by a reconciler after the command, so a refetch
      // the command triggered would race it and redraw the host as bound
      // to nobody. The Web addresses section draws its chips from the
      // document, which is exact, and reads this only for what the
      // document cannot say. A grant the operator surface makes does
      // invalidate it, because that write is the row itself.
      providesTags: (_result, _error, arg) => [{ type: 'TenantDomains' as const, id: arg.tenant }],
    },

    getWebsiteTraffic: {
      // Keyed by site, and invalidated by the operator surface's writes
      // to the same site's binding (see `operator.ts`), which is the only
      // thing that moves the card's state while it is open. The rows a
      // collection writes arrive overnight, and the card is read afresh
      // on the next visit.
      providesTags: (_result, _error, arg) => [siteTrafficTag(arg.tenant, arg.website)],
    },

    getAuthoringComponentRevision: {
      // A past revision is immutable — that is what makes `(id, revision)`
      // a pin — so once read it never needs reading again.
      keepUnusedDataFor: Infinity,
    },

    listPublications: {
      providesTags: (_result, _error, arg) => [{ type: 'Publications' as const, id: `${arg.tenant}/${arg.website}` }],
    },

    listWebhooks: {
      providesTags: (_result, _error, arg) => [{ type: 'Webhooks' as const, id: arg.tenant }],
    },
    createWebhook: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Webhooks' as const, id: arg.tenant }],
    },
    updateWebhook: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Webhooks' as const, id: arg.tenant }],
    },
    deleteWebhook: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Webhooks' as const, id: arg.tenant }],
    },
    listWebhookDeliveries: {
      providesTags: (_result, _error, arg) => [{ type: 'WebhookDeliveries' as const, id: `${arg.tenant}/${arg.webhook}` }],
    },
    testWebhook: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'WebhookDeliveries' as const, id: `${arg.tenant}/${arg.webhook}` }],
    },
    redeliverWebhook: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'WebhookDeliveries' as const, id: `${arg.tenant}/${arg.webhook}` }],
    },

    applyCommand: {
      /**
       * **The response is the cache update.**
       *
       * A command answers with the whole updated document, in the same
       * schema a read returns, because both are one `Evolve` folded from
       * one stream. So the write's own answer is written straight into
       * the read's cache entry: no refetch, no invalidation, and above
       * all no optimistic-update layer reimplementing the fold in
       * TypeScript. A second model of the document is exactly what the
       * one-intent-per-control rule exists to prevent.
       *
       * Note what is still deliberately *not* invalidated: the listing.
       * It is the one authoring read served from the projection, so it
       * lags, and a refetch triggered here would race the projection
       * and redraw the row with the revision before the one just
       * written. A listing is a hint; the document is the fact — so it
       * is *patched* from the fact rather than re-asked for. See
       * `reflectInListings`.
       */
      async onQueryStarted({ tenant, id }, { dispatch, getState, queryFulfilled }) {
        const data = await answered(queryFulfilled)
        if (!data) return
        dispatch(authoringApi.util.upsertQueryData('getAuthoringComponent', { tenant, id }, data))
        // …and into every listing already holding a row for it, from
        // the same answer, for the reason given on `reflectInListings`.
        reflectInListings(data, tenant, dispatch, getState)
        // And the one write that changes more than a document: editing a
        // **type definition** changes what the tenant's manifest says.
        invalidateManifestIfDefinition(data, tenant, dispatch, getState)
      },
    },

    createComponent: {
      /**
       * Likewise: a create answers with the component at its first
       * revision, so the new document is readable before the projection
       * has heard of it. That is the whole reason the listing is not
       * where a client looks for what it just made.
       */
      async onQueryStarted({ tenant }, { dispatch, getState, queryFulfilled }) {
        const data = await answered(queryFulfilled)
        if (!data) return
        dispatch(authoringApi.util.upsertQueryData('getAuthoringComponent', { tenant, id: data.id }, data))
        reflectInListings(data, tenant, dispatch, getState)
        // …and re-read whatever else the reader is holding, because a
        // create is the one write whose effects the response does not
        // describe. See `rereadTheTenantsOtherDocuments`.
        rereadTheTenantsOtherDocuments(data, tenant, dispatch, getState)
        invalidateManifestIfDefinition(data, tenant, dispatch, getState)
      },
    },

    finaliseUpload: {
      /**
       * And again, because finalising **is** a command — it reaches
       * `write.Apply` with an event the type constructed, appends one
       * revision, and answers with the whole updated document, exactly as
       * `applyCommand` does.
       *
       * The one difference is who wrote the event. The intent it appends
       * is withheld from the manifest, so this client could not have sent
       * it: every member is a reading the platform took off the object.
       * That changes nothing about the answer, which is the point — a
       * blob replacement is an ordinary revision, and treating it as
       * anything else is how a second write path starts.
       */
      async onQueryStarted({ tenant, id }, { dispatch, getState, queryFulfilled }) {
        const data = await answered(queryFulfilled)
        if (!data) return
        dispatch(authoringApi.util.upsertQueryData('getAuthoringComponent', { tenant, id }, data))
        reflectInListings(data, tenant, dispatch, getState)
      },
    },
  },
})

/**
 * The write's answer, or nothing when it was refused.
 *
 * **A refusal is the caller's to show, and it already does.** The hook
 * hands the rejected result to the screen that sent it, which draws the
 * platform's reason beside the control. `onQueryStarted` is a second
 * listener on the same promise, and awaiting it bare turned every
 * refusal into an uncaught exception as well — the author saw an
 * accurate message and the page threw underneath it. Here a refusal
 * means only that there is nothing to write into the cache.
 */
async function answered(queryFulfilled: Promise<{ data: Document }>): Promise<Document | undefined> {
  try {
    return (await queryFulfilled).data
  } catch {
    return undefined
  }
}

/**
 * Write the component into every listing already holding one.
 *
 * **This is the other half of "the response is the cache update", and
 * it was missing.** Not invalidating the listing is right — it is the
 * projection's, it lags, and re-asking for it races the reconciler. But
 * "do not re-ask" was taken to mean "do nothing", and the consequence
 * was the bug an author actually hits: create an album, go back to the
 * albums, and it is not there; rename a page and the row still says
 * what it said before. The cache entry is held for a minute, so the
 * surface stayed wrong for a minute and a browser reload was the only
 * way to move it. A list that is only correct after a reload is a list
 * nobody can trust, which costs more than the staleness it was
 * protecting against.
 *
 * So the same authoritative answer that updates the document updates
 * the rows derived from it. No refetch, so nothing races; no optimistic
 * layer, because the row is read off the response rather than guessed
 * from the request. `ComponentSummary` is `Document` less the parts a
 * listing does not carry, which is why this is a projection and not a
 * reimplementation.
 *
 * **Where a row is placed is decided, not assumed.** A listing is
 * ordered by type and then by id and it is paginated, so a new row
 * belongs on exactly one page: the one whose rows straddle it, or the
 * last one if it sorts past everything. A page that ends before it and
 * has a cursor is a page the row is *not* on, and pushing it there
 * would invent a duplicate that survives until the entry expires.
 */
function reflectInListings(
  document: Document,
  tenant: string,
  dispatch: (action: unknown) => unknown,
  getState: () => unknown,
) {
  const row: ComponentSummary = {
    id: document.id,
    type: document.type,
    label: document.label,
    revision: document.revision,
  }

  // Both the listing narrowed to this type and the unfiltered one —
  // the palette and the site switcher read those two separately, and a
  // row written into one of them only is the navigation disagreeing
  // with the screen it navigated to.
  const entries = authoringApi.util.selectInvalidatedBy(getState() as never, [
    { type: 'ComponentList', id: `${tenant}/${document.type}` },
    { type: 'ComponentList', id: `${tenant}/*` },
  ])

  for (const { endpointName, originalArgs } of entries) {
    if (endpointName !== 'listComponents') continue
    dispatch(
      authoringApi.util.updateQueryData('listComponents', originalArgs, (draft) => {
        const at = draft.components.findIndex((candidate) => candidate.id === row.id)
        if (at >= 0) {
          draft.components[at] = row
          return
        }
        const before = draft.components.findIndex((candidate) => sortsAfter(candidate, row))
        if (before >= 0) {
          draft.components.splice(before, 0, row)
          return
        }
        // Past the end of this page. It belongs here only if there is
        // no page after this one.
        if (!draft.cursor) draft.components.push(row)
      }),
    )
  }
}

/**
 * Re-read the tenant's other open documents after a create.
 *
 * **A create is the one write that changes documents it does not
 * return.** Every command is one intent against one aggregate, so its
 * response is the whole of its effect and writing it into the cache is
 * exact. A create is not: the server composes. Making a record also
 * makes the Gallery it owns *and adds it to the Collection its type
 * names* — the dialog sends fields and nothing else, and says so. The
 * answer is the new record, which mentions none of that.
 *
 * That is the bug an author hits as "it is not there until I refresh":
 * the screen that lists albums does not read a listing at all, it reads
 * the Collection document, and the Collection's cached copy still said
 * what it said before the album existed. No tag covered it, because no
 * tag could — this client cannot know from the response which documents
 * the server touched.
 *
 * So it stops trying to know and re-reads the ones the reader is
 * actually holding, which is a small and bounded set: cache entries,
 * not the tenant. **Refetching is safe here in a way it is not for a
 * listing** — a component read is folded from the stream rather than
 * served from the projection, so there is no reconciler to race and the
 * answer that comes back cannot be older than the write.
 *
 * The new document itself is excluded: it was just written from the
 * response, and asking again would be a round trip to be told what the
 * client already holds.
 */
function rereadTheTenantsOtherDocuments(
  document: Document,
  tenant: string,
  dispatch: (action: unknown) => unknown,
  getState: () => unknown,
) {
  const cached = authoringApi.util.selectCachedArgsForQuery(getState() as never, 'getAuthoringComponent')
  const stale = cached
    .filter((arg) => arg.tenant === tenant && arg.id !== document.id)
    .map((arg) => componentTag(arg.tenant, arg.id))
  if (stale.length > 0) dispatch(authoringApi.util.invalidateTags(stale))
}

/** The listing's own order — by type, then by id — asked as a comparison. */
function sortsAfter(candidate: ComponentSummary, row: ComponentSummary): boolean {
  if (candidate.type !== row.type) return candidate.type > row.type
  return candidate.id > row.id
}

/**
 * Re-read the tenant's manifest when the write was a **type
 * definition**.
 *
 * It reads the type off the response and asks the *cached manifest*
 * whether that type declares types — never a name written here. That is
 * the same rule the palette and the uploader follow: this client holds no
 * component type's name, including the meta-type's.
 *
 * It runs in `onQueryStarted` rather than `invalidatesTags` because the
 * decision needs the manifest that is already in cache, and only this
 * hook is handed the store. A cache miss invalidates nothing, which is
 * the safe direction: nothing can have edited a definition without
 * having read the manifest that told it where the type designer is.
 */
function invalidateManifestIfDefinition(
  document: Document,
  tenant: string,
  dispatch: (action: unknown) => unknown,
  getState: () => unknown,
) {
  const cached = authoringApi.endpoints.getManifest.select({ tenant })(getState() as never)
  if (metaTypeName(cached.data) === document.type) {
    dispatch(authoringApi.util.invalidateTags([manifestTag(tenant)]))
  }
}

/**
 * The type whose documents declare component types, as the manifest says
 * it — and undefined for a build that ships none.
 *
 * **Asked, never known.** The meta-type has a name and this client
 * deliberately does not hold it: `defines` is published for exactly the
 * reason `acceptsUpload` is, so that a client offers a type designer by
 * reading the registry rather than by agreeing with it out of band.
 */
export function metaTypeName(manifest: Manifest | undefined): string | undefined {
  return manifest?.types.find((declared) => declared.defines)?.name
}

/**
 * The type whose documents declare patterns, on the same terms.
 *
 * The second meta-type, asked for rather than known for the same reason
 * the first is: `declaresPatterns` is published so that a client offers
 * a pattern designer by reading the registry, and `"ComponentPattern"`
 * appears nowhere in this client. `editors/index` already asks this
 * question of a loaded document's type; the navigation and the library
 * ask it of the tenant, which is the same question one level up.
 */
export function patternTypeName(manifest: Manifest | undefined): string | undefined {
  return manifest?.types.find((declared) => declared.declaresPatterns)?.name
}

export const {
  useGetManifestQuery,
  useListTenantsQuery,
  useListComponentsQuery,
  useGetAuthoringComponentQuery,
  useGetAuthoringComponentRevisionQuery,
  useLazyGetAuthoringComponentQuery,
  useLazyGetAuthoringComponentRevisionQuery,
  useApplyCommandMutation,
  useCreateComponentMutation,
  useListUsagesQuery,
  useListTenantDomainsQuery,
  useGrantUploadMutation,
  useFinaliseUploadMutation,
  useListPublicationsQuery,
  usePublishWebsiteMutation,
  useGetPublicationQuery,
  usePromotePublicationMutation,
  useCheckRendererCoverageMutation,
  useLazyGetRendererContractQuery,
  useLazyGetAuthoringOpenApiyamlQuery,
  useGetPublicationChangesQuery,
  useGetPublicationOperationQuery,
  useListEventTypesQuery,
  useListWebhooksQuery,
  useCreateWebhookMutation,
  useGetWebhookQuery,
  useUpdateWebhookMutation,
  useDeleteWebhookMutation,
  useRotateWebhookSecretMutation,
  useTestWebhookMutation,
  useListWebhookDeliveriesQuery,
  useRedeliverWebhookMutation,
  useListSubmissionsQuery,
  useGetSubmissionQuery,
  useJudgeSubmissionMutation,
  useResendSubmissionMutation,
  useGetWebsiteTrafficQuery,
} = api

export type {
  Child,
  ChildInput,
  Command,
  ComponentSummary,
  Document,
  Manifest,
  ManifestBinding,
  ManifestIntent,
  ManifestReference,
  ManifestType,
  Membership,
  Publication,
  PublicationChange,
  PublicationChanges,
  PublicationOperation,
  PublicationSelection,
  PublicationState,
  PublicationSummary,
  Publications,
  Reference,
  RendererManifest,
  RendererCoverage,
  ReferenceInput,
  Usage,
  Webhook,
  WebhookDelivery,
  EventType,
  Submission,
  SubmissionSummary,
  Submissions,
  SubmissionJudgement,
  TrafficDay,
  TrafficReport,
} from './authoringApi'
